import { DeleteOutlined, PlusCircleOutlined } from '@ant-design/icons';
import { Button, Divider, Table, Typography } from 'antd';
import { FieldArray, useField } from 'formik';
import { useTranslation } from 'react-i18next';
import { type MobilityKind } from '../../../../api/types';
import CompanyCurrencyField from '../../../../components/fields/CompanyCurrencyField';
import TranslatedInputField from '../../../../components/fields/TranslatedInputField';
import { type MobilityFormValues } from './types';
import { TitleAndActionContainer } from './ui';

type AddOnOptionsFieldArrayProps = {
    name?: string;
    isChargeable: boolean;
    disabled?: boolean;
};

type FieldValue = Extract<MobilityFormValues, { kind: MobilityKind.Addon }>['options'];

const AddOnOptionsFieldArray = ({ name = 'options', isChargeable, disabled = false }: AddOnOptionsFieldArrayProps) => {
    const { t } = useTranslation('mobilityDetails');

    const [field] = useField<FieldValue>(name);
    const { value } = field;

    return (
        <FieldArray
            name={name}
            render={({ remove, push }) => {
                const renderActions = (index: number) =>
                    !disabled && (
                        <Button
                            icon={<DeleteOutlined />}
                            onClick={() => {
                                remove(index);
                            }}
                            size="middle"
                            type="link"
                        />
                    );

                return (
                    <>
                        <TitleAndActionContainer>
                            <Typography.Title level={5} style={{ fontSize: 14 }}>
                                {t('mobilityDetails:fields.option.label')}
                            </Typography.Title>
                        </TitleAndActionContainer>
                        <Divider />
                        <Table
                            dataSource={value}
                            pagination={false}
                            rowKey={(row, index: number) => `${name}_${index.toString()}`}
                        >
                            <Table.Column
                                dataIndex="name"
                                render={(value, record, index) => (
                                    <TranslatedInputField disabled={disabled} name={`${name}[${index}].name`} />
                                )}
                                title={t('mobilityDetails:fields.optionName.label')}
                            />
                            {isChargeable && (
                                <Table.Column
                                    dataIndex="price"
                                    render={(value, record, index) => (
                                        <CompanyCurrencyField disabled={disabled} name={`${name}[${index}].price`} />
                                    )}
                                    title={t('mobilityDetails:fields.optionPrice.label')}
                                />
                            )}
                            <Table.Column key="actions" align="center" render={renderActions} width={100} />
                        </Table>
                        <div style={{ marginTop: '10px' }}>
                            {!disabled && (
                                <Button
                                    icon={<PlusCircleOutlined />}
                                    onClick={() => push({ name: { defaultValue: '', overrides: [] } })}
                                    type="link"
                                >
                                    {t('mobilityDetails:actions.addOption')}
                                </Button>
                            )}
                        </div>
                    </>
                );
            }}
        />
    );
};

export default AddOnOptionsFieldArray;
